/**
 * Connections Store
 * Manages connection-related state shared across components
 */

import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useApi } from '@/composables/useApi';

export const useConnectionsStore = defineStore('connections', () => {
  // State
  const pendingCount = ref(0);
  const loading = ref(false);
  const lastFetched = ref(null);

  // Cache duration in milliseconds (30 seconds)
  const CACHE_DURATION = 30000;

  /**
   * Fetch pending connections count
   * Uses caching to prevent duplicate API calls
   * @param {boolean} force - Force refresh even if cached
   */
  async function fetchPendingCount(force = false) {
    // Check if we have a recent cached value
    if (!force && lastFetched.value) {
      const elapsed = Date.now() - lastFetched.value;
      if (elapsed < CACHE_DURATION) {
        return pendingCount.value;
      }
    }

    // Prevent concurrent fetches
    if (loading.value) {
      return pendingCount.value;
    }

    const api = useApi();
    loading.value = true;

    try {
      const response = await api.get('/api/connections/pending/count');
      if (response.success) {
        pendingCount.value = response.count || 0;
        lastFetched.value = Date.now();
      }
      return pendingCount.value;
    } catch (error) {
      console.error('Failed to fetch pending connections count:', error);
      return pendingCount.value;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Increment pending count (when a new request is received)
   */
  function incrementPendingCount() {
    pendingCount.value++;
  }

  /**
   * Decrement pending count (when a request is accepted/rejected)
   */
  function decrementPendingCount() {
    if (pendingCount.value > 0) {
      pendingCount.value--;
    }
  }

  /**
   * Reset store state
   */
  function $reset() {
    pendingCount.value = 0;
    loading.value = false;
    lastFetched.value = null;
  }

  return {
    // State
    pendingCount,
    loading,

    // Actions
    fetchPendingCount,
    incrementPendingCount,
    decrementPendingCount,
    $reset
  };
});
